// Thin client over the API worker. The bearer token + base URL come from localStorage,
// set on the login screen. We don't use a fancier client (react-query / swr) yet —
// the dashboard is small, useEffect+useState is fine. Swap in later if it grows.

export interface Settings {
    apiBase: string;
    token: string;
}

const STORAGE_KEY = "okiru.settings.v1";

export function loadSettings(): Settings | null {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw) as Settings;
        if (!parsed.apiBase || !parsed.token) return null;
        return parsed;
    } catch {
        return null;
    }
}

export function saveSettings(s: Settings): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
}

export function clearSettings(): void {
    localStorage.removeItem(STORAGE_KEY);
}

export class ApiError extends Error {
    constructor(public status: number, message: string) {
        super(message);
    }
}

export async function apiGet<T>(path: string, settings: Settings): Promise<T> {
    const url = new URL(path, settings.apiBase).toString();
    const res = await fetch(url, {
        headers: { Authorization: `Bearer ${settings.token}` },
    });
    if (!res.ok) {
        const text = await res.text();
        throw new ApiError(res.status, text || res.statusText);
    }
    return (await res.json()) as T;
}

// Typed metric calls — mirror the API worker's response shapes.
export interface RevenueResult {
    range: { from: string; to: string };
    currency: string;
    series: { day: string; order_count: number; subtotal_revenue: number; net_revenue: number }[];
    totals: { order_count: number; subtotal_revenue: number; net_revenue: number };
}
export interface AOVResult {
    range: { from: string; to: string };
    currency: string;
    order_count: number;
    revenue: number;
    aov: number;
}
export interface TopProductsResult {
    range: { from: string; to: string };
    items: { product_id: string | null; title: string; units_sold: number; revenue: number }[];
}
export interface CustomerSummary {
    range: { from: string; to: string };
    new_customers: number;
    returning_customers: number;
    total_customers: number;
    repeat_rate: number;
}

export interface ROASResult {
    range: { from: string; to: string };
    currency: string;
    blended: { spend: number; revenue: number; roas: number; attributed_orders: number };
    series: { day: string; spend: number; revenue: number; roas: number }[];
    by_campaign: { campaign_id: string; campaign_name: string; spend: number; revenue: number; roas: number }[];
    unattributed: { revenue: number; orders: number };
}

export const fetchROAS = (s: Settings, from: string, to: string) =>
    apiGet<ROASResult>(`/api/metrics/roas?from=${from}&to=${to}`, s);

export interface TopCampaignsResult {
    range: { from: string; to: string };
    currency: string;
    items: { campaign_id: string; campaign_name: string; spend: number; revenue: number; roas: number; orders: number }[];
}

export const fetchTopCampaigns = (s: Settings, from: string, to: string, limit = 20) =>
    apiGet<TopCampaignsResult>(`/api/metrics/campaigns?from=${from}&to=${to}&limit=${limit}`, s);

export interface RecentLineItem {
    order_id: string;
    order_name: string;
    processed_at: string;
    customer_name: string | null;
    customer_email: string | null;
    financial_status: string | null;
    fulfillment_status: string | null;
    product_id: string | null;
    variant_id: string | null;
    title: string;
    variant_title: string | null;
    sku: string | null;
    quantity: number;
}
export interface RecentLineItemsResult {
    days: number;
    items: RecentLineItem[];
}

export const fetchRevenue = (s: Settings, from: string, to: string) =>
    apiGet<RevenueResult>(`/api/metrics/revenue?from=${from}&to=${to}`, s);
export const fetchAOV = (s: Settings, from: string, to: string) =>
    apiGet<AOVResult>(`/api/metrics/aov?from=${from}&to=${to}`, s);
export const fetchTopProducts = (s: Settings, from: string, to: string, limit = 10) =>
    apiGet<TopProductsResult>(`/api/metrics/top-products?from=${from}&to=${to}&limit=${limit}`, s);
export const fetchCustomerSummary = (s: Settings, from: string, to: string) =>
    apiGet<CustomerSummary>(`/api/metrics/customers/summary?from=${from}&to=${to}`, s);
export const fetchRecentLineItems = (s: Settings, days: number) =>
    apiGet<RecentLineItemsResult>(`/api/orders/recent?days=${days}`, s);

export interface UnattributedOrder {
    id: string;
    name: string;
    processed_at: string;
    total_price: number;
    currency: string;
    landing_site: string | null;
    referring_site: string | null;
}

export interface UnattributedOrdersResult {
    range: { from: string; to: string };
    items: UnattributedOrder[];
}

export const fetchUnattributedOrders = (s: Settings, from: string, to: string) =>
    apiGet<UnattributedOrdersResult>(`/api/orders/unattributed?from=${from}&to=${to}`, s);

/** Set/update/clear manual attribution for one or many orders.
 *  Pass `campaign_id = null` to clear. Returns the count of rows touched. */
export async function saveAttributions(
    settings: Settings,
    orderIds: string[],
    campaignId: string | null
): Promise<{ updated: number }> {
    const url = new URL("/api/attributions", settings.apiBase).toString();
    const res = await fetch(url, {
        method: "POST",
        headers: {
            Authorization: `Bearer ${settings.token}`,
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ order_ids: orderIds, campaign_id: campaignId }),
    });
    if (!res.ok) {
        const text = await res.text();
        throw new ApiError(res.status, text || res.statusText);
    }
    return (await res.json()) as { updated: number };
}
